(() => {
  if (customElements.get('nx-duo-product')) return;
  const requests = new Map();
  const json = (url) => {
    if (!requests.has(url)) requests.set(url, fetch(url, {credentials: 'same-origin', headers: {Accept: 'application/json'}}).then(response => {
      if (!response.ok) throw new Error('Unavailable');
      return response.json();
    }).catch(error => { requests.delete(url); throw error; }));
    return requests.get(url);
  };
  const pairKey = ids => ids.map(String).sort((a, b) => Number(a) - Number(b)).join(':');
  const duoPrice = sum => Math.max(0, Math.floor((sum * 85 - 9700) / 10000) * 100 + 97);
  const isPrice = amount => Number.isSafeInteger(amount) && amount >= 0;
  const value = option => typeof option === 'object' ? option.value ?? option.name : option;
  const colorKeys = {"Beige sable": 'sand_beige', Blanc: 'white', Gris: 'grey', "Vert d'eau": 'seafoam_green', Bleu: 'blue', 'Gris clair': 'light_grey', Ivoire: 'ivory', Rose: 'pink', Taupe: 'taupe', 'Vert canard': 'teal', 'Gris perle': 'pearl_grey', Naturel: 'natural', 'Vert sauge': 'sage_green', Champagne: 'champagne', 'Gris foncé': 'dark_grey', 'Bleu foncé': 'dark_blue', 'Gris argenté': 'silver_grey', Kaki: 'khaki'};
  const imageUrl = source => typeof source === 'string' ? source : source?.src || source?.url || '';
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  class NoctuaDuo extends HTMLElement {
    async connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.strings = JSON.parse(this.querySelector('[data-duo-strings]').textContent);
      this.status = this.querySelector('[data-duo-status]');
      this.addButton = this.querySelector('[data-duo-add]');
      this.configuration = this.querySelector('[data-duo-configuration]');
      this.selections = [{}, {}];
      this.busy = false;
      this.canBuy = false;
      this.addButton.disabled = true;
      this.addButton.addEventListener('click', () => this.add(), {signal: this.abort.signal});
      try {
        this.catalog = JSON.parse(JSON.stringify(await json(this.dataset.catalogUrl)));
        if (!this.isConnected) return;
        this.localizeCatalog();
        this.currency = this.dataset.currency || this.catalog.currency || 'EUR';
        this.root = `${(this.dataset.root || '/').replace(/\/$/, '')}/`;
        this.parentPrices = new Map();
        this.parentRequests = new Map();
        this.money = new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {style: 'currency', currency: this.currency});
        this.model = this.catalog.pillowModels.find(model => model.handle === this.dataset.productHandle) || this.catalog.pillowModels.find(model => model.key === this.dataset.model) || this.catalog.pillowModels[0];
        this.mode = this.dataset.mode;
        if (this.mode === 'auto') this.mode = this.catalog.pillowModels.some(model => model.handle === this.dataset.productHandle) ? 'pillows' : 'cases';
        this.bundleMap = new Map(this.catalog.caseBundles.map(bundle => [pairKey(bundle.components), bundle]));
        this.preview = this.catalog.purchaseEnabled !== true;
        this.querySelector('[data-duo-preview]').hidden = !this.preview;
        this.useMarketPrices = !this.preview || this.currency !== this.catalog.currency;
        this.currencySupported = !this.useMarketPrices;
        const context = this.querySelector('[data-duo-context]');
        if (context) {
          try {
            const quote = JSON.parse(context.textContent);
            const handle = this.mode === 'pillows' ? this.model.handle : this.catalog.caseProductHandle;
            if (this.validContext(quote, quote.variant?.id, handle)) this.parentPrices.set(String(quote.variant.id), quote.variant);
          } catch (_) {}
        }
        this.dataFresh = true;
        if (this.useMarketPrices) this.dataFresh = await this.refreshSources();
        if (!this.isConnected) return;
        if (this.mode === 'pillows') this.renderPillows();
        else this.renderCases();
        this.renderDescription();
        this.update();
      } catch (_) {
        this.status.textContent = this.strings.load_error;
        this.status.dataset.error = '';
        this.addButton.textContent = this.strings.unavailable;
      }
    }
    disconnectedCallback() { this.abort?.abort(); this.initialized = false; this.canBuy = false; }
    moneyText(cents) { return isPrice(cents) ? this.money.format(cents / 100) : '—'; }
    localizeCatalog() {
      this.catalog.pillowModels.forEach(model => {
        model.title = this.strings[`model_${model.key}_title`] || model.title;
        model.description = this.strings[`model_${model.key}_description`] || model.description;
        model.descriptionHtml = this.strings[`model_${model.key}_description_html`] || model.descriptionHtml;
      });
      this.catalog.materials.forEach(material => {
        material.title = this.strings[`material_${material.key}_title`] || material.title;
        material.description = this.strings[`material_${material.key}_description`] || material.description;
      });
      this.catalog.caseDescriptionHtml = this.strings.case_description_html || this.catalog.caseDescriptionHtml;
    }
    colorLabel(color) { return this.strings[`color_${colorKeys[color]}`] || color; }
    async refreshSources() {
      const handles = this.mode === 'pillows' ? [this.model.sourceHandle].filter(Boolean) : this.catalog.materials.map(material => material.handle).filter(Boolean);
      if (!handles.length) return false;
      try {
        const cart = await json(`${this.root}cart.js`);
        this.currencySupported = cart.currency === this.currency && /^[A-Z]{2}$/.test(this.dataset.country || '');
        if (!this.currencySupported) return false;
        const products = await Promise.all(handles.map(handle => json(`${this.root}products/${encodeURIComponent(handle)}.js`)));
        this.sourceVariants = new Map(products.flatMap(product => product.variants.filter(variant => isPrice(variant.price)).map(variant => [String(variant.id), variant])));
        if (this.mode === 'cases') this.catalog.materials.forEach(material => material.variants.forEach(variant => {
          const fresh = this.sourceVariants.get(String(variant.id));
          variant.available = fresh ? fresh.available : false;
          if (fresh) { variant.price = fresh.price; variant.image = imageUrl(fresh.featured_image) || variant.image; }
        }));
        return true;
      } catch (_) { return false; }
    }
    validContext(quote, variantId, handle) {
      return quote?.currency === this.currency && quote.country === this.dataset.country && /^[A-Z]{2}$/.test(quote.country || '') && quote.productHandle === handle && /^\d+$/.test(String(variantId || '')) && String(quote.variant?.id) === String(variantId) && isPrice(quote.variant?.price) && typeof quote.variant?.available === 'boolean';
    }
    loadParentPrice(bundle) {
      const id = String(bundle.variantId);
      if (this.parentPrices.has(id) || this.parentRequests.has(id) || !this.dataset.sectionId) return;
      const handle = this.mode === 'pillows' ? this.model.handle : this.catalog.caseProductHandle;
      const params = new URLSearchParams({variant: id, section_id: this.dataset.sectionId});
      const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
      if (previewTheme && /^\d+$/.test(previewTheme)) params.set('preview_theme_id', previewTheme);
      const signal = this.abort.signal;
      const request = fetch(`${this.root}products/${encodeURIComponent(handle)}?${params}`, {credentials: 'same-origin', headers: {Accept: 'text/html'}, signal})
        .then(response => {
          if (!response.ok) throw new Error('Unavailable');
          return response.text();
        }).then(html => {
          const context = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-duo-context]');
          const quote = JSON.parse(context?.textContent || 'null');
          if (!this.validContext(quote, id, handle)) throw new Error('Invalid price context');
          if (!signal.aborted) this.parentPrices.set(id, quote.variant);
        }).catch(() => {
          if (!signal.aborted) this.parentPrices.set(id, null);
        }).finally(() => {
          if (!signal.aborted && this.isConnected && String(this.selectedBundle?.variantId) === id) this.update();
        });
      this.parentRequests.set(id, request);
    }
    setImage(container, url, alt) {
      const src = imageUrl(url);
      if (!src) { container.replaceChildren(); return; }
      let image = container.querySelector('img');
      if (!image) {
        image = element('img');
        image.width = 1000;
        image.height = 1000;
        image.loading = 'eager';
        image.decoding = 'async';
        container.replaceChildren(image);
      }
      if (image.getAttribute('src') !== src) image.src = src;
      image.alt = alt;
    }
    renderPillows() {
      if (!this.model?.variants?.length) throw new Error('Missing model');
      this.dataset.kind = 'pillows';
      this.querySelector('[data-duo-title]').textContent = this.dataset.productTitle || this.model.title;
      this.querySelector('[data-duo-intro]').textContent = this.model.description || this.strings.pillow_intro;
      this.querySelector('[data-duo-hero]').hidden = false;
      this.querySelector('[data-duo-selection-note]').hidden = true;
      this.querySelector('[data-duo-gallery-note]').textContent = this.strings.pillow_gallery_note;
      this.setImage(this.querySelector('[data-duo-hero]'), this.model.image, this.model.title);
      const fieldset = element('fieldset', 'nx-duo__card');
      fieldset.append(element('legend', '', this.strings.pillow_size));
      const sizes = element('div', 'nx-duo__pillow-sizes');
      const requested = new URLSearchParams(location.search).get('variant') || this.dataset.selectedVariant;
      this.selectedPillow = this.model.variants.find(variant => String(variant.id) === requested) || this.model.variants.find(variant => variant.available !== false) || this.model.variants[0];
      this.model.variants.forEach(variant => {
        const label = element('label', 'nx-duo__size');
        const input = element('input');
        input.type = 'radio';
        input.name = `duo-size-${this.dataset.sectionId}`;
        input.value = String(variant.id);
        input.checked = variant === this.selectedPillow;
        input.disabled = variant.available === false;
        input.addEventListener('change', () => { this.selectedPillow = variant; this.update(); }, {signal: this.abort.signal});
        label.append(input, element('span', '', `${variant.size} cm`));
        sizes.append(label);
      });
      fieldset.append(sizes);
      this.configuration.replaceChildren(fieldset);
      const composition = this.querySelector('[data-duo-composition]');
      composition.replaceChildren(element('p', '', this.strings.pillows_composition));
    }
    makeField(labelText, className) {
      const label = element('label', `nx-duo__field ${className || ''}`);
      label.append(element('span', '', labelText));
      const select = element('select');
      label.append(select);
      return {label, select};
    }
    fillSelect(select, choices, chosen) {
      select.replaceChildren(...choices.map(choice => {
        const option = element('option', '', choice.label ?? choice.value);
        option.value = String(choice.value);
        option.disabled = choice.disabled === true;
        option.selected = String(choice.value) === String(chosen);
        return option;
      }));
    }
    renderCases() {
      if (!this.catalog.materials?.length) throw new Error('Missing materials');
      this.dataset.kind = 'cases';
      this.querySelector('[data-duo-title]').textContent = this.dataset.productTitle || this.strings.case_title;
      this.querySelector('[data-duo-intro]').textContent = this.strings.case_intro;
      this.querySelector('[data-duo-hero]').hidden = false;
      this.querySelector('[data-duo-selection-note]').hidden = false;
      this.querySelector('[data-duo-gallery-note]').textContent = this.strings.gallery_note;
      this.cards = [];
      this.configuration.replaceChildren();
      const requestedId = new URLSearchParams(location.search).get('variant') || this.dataset.selectedVariant;
      const requestedBundle = this.catalog.caseBundles.find(bundle => String(bundle.variantId) === String(requestedId));
      for (let index = 0; index < 2; index += 1) {
        const card = element('fieldset', 'nx-duo__card nx-duo__card--case');
        const legend = element('legend');
        const preview = element('span', 'nx-duo__selection-preview');
        preview.dataset.duoPiece = String(index);
        const previewImage = element('span', 'nx-duo__selection-image');
        previewImage.dataset.duoPieceImage = '';
        const previewCaption = element('span', 'visually-hidden');
        previewCaption.dataset.duoPieceCaption = '';
        preview.append(previewImage, previewCaption);
        const unitPrice = element('span', 'nx-duo__unit-reference visually-hidden');
        legend.append(preview, element('span', 'nx-duo__selection-title', this.strings.case_number.replace('__NUMBER__', index + 1)), unitPrice);
        card.append(legend);
        const fields = element('div', 'nx-duo__fields');
        const material = this.makeField(this.strings.material, 'nx-duo__field--material');
        const color = this.makeField(this.strings.color);
        const size = this.makeField(this.strings.size);
        fields.append(material.label, color.label, size.label);
        const note = element('p', 'nx-duo__material-note');
        card.append(fields, note);
        this.configuration.append(card);
        this.cards.push({card, material: material.select, color: color.select, size: size.select, note, unitPrice, previewImage, previewCaption});
        const requestedSourceId = requestedBundle?.components[index];
        const requestedMaterial = requestedSourceId && this.catalog.materials.find(entry => entry.variants.some(variant => String(variant.id) === String(requestedSourceId)));
        const initialMaterial = requestedMaterial || this.catalog.materials.find(entry => entry.variants.some(variant => variant.available !== false)) || this.catalog.materials[0];
        const requestedVariant = requestedMaterial?.variants.find(variant => String(variant.id) === String(requestedSourceId));
        if (requestedVariant) this.selections[index] = {material: requestedMaterial, variant: requestedVariant, color: requestedVariant.color, size: requestedVariant.size};
        this.fillSelect(material.select, this.catalog.materials.map(entry => ({value: entry.key, label: entry.title})), initialMaterial.key);
        material.select.addEventListener('change', () => this.selectMaterial(index), {signal: this.abort.signal});
        color.select.addEventListener('change', () => this.selectColor(index), {signal: this.abort.signal});
        size.select.addEventListener('change', () => this.selectSize(index), {signal: this.abort.signal});
        this.selectMaterial(index, false);
        if (requestedVariant) {
          this.selections[index] = {material: requestedMaterial, variant: requestedVariant, color: requestedVariant.color, size: requestedVariant.size};
          color.select.value = requestedVariant.color;
          const matchingColorVariants = requestedMaterial.variants.filter(variant => variant.color === requestedVariant.color);
          this.fillSelect(size.select, matchingColorVariants.map(variant => ({value: variant.size, label: `${variant.size} cm`, disabled: variant.available === false})), requestedVariant.size);
        }
      }
    }
    selectMaterial(index, update = true) {
      const card = this.cards[index];
      const material = this.catalog.materials.find(entry => entry.key === card.material.value);
      const old = this.selections[index];
      const available = material.variants.filter(variant => variant.available !== false);
      const initial = available.find(variant => variant.size === old.size && variant.color === old.color) || available.find(variant => variant.size === old.size) || available.find(variant => String(variant.color).toLowerCase() === 'blanc') || available[0] || material.variants[0];
      this.selections[index] = {material, color: initial.color, size: initial.size, variant: initial};
      const colors = [...new Set(material.variants.map(variant => variant.color))];
      this.fillSelect(card.color, colors.map(color => ({value: color, label: this.colorLabel(color), disabled: !material.variants.some(variant => variant.color === color && variant.available !== false)})), initial.color);
      card.note.textContent = material.description || '';
      card.note.hidden = !material.description;
      this.fillSizes(index);
      if (update) this.update();
    }
    renderDescription() {
      const content = this.querySelector('[data-duo-description]');
      if (!content.textContent.trim()) {
        const html = this.mode === 'pillows' ? this.model.descriptionHtml : this.catalog.caseDescriptionHtml;
        if (html) content.innerHTML = html;
      }
      this.querySelector('[data-duo-story]').hidden = !content.textContent.trim();
    }
    fillSizes(index) {
      const selection = this.selections[index];
      const variants = selection.material.variants.filter(variant => variant.color === selection.color);
      const valid = variants.find(variant => variant.size === selection.size && variant.available !== false) || variants.find(variant => variant.available !== false) || variants[0];
      selection.variant = valid;
      selection.size = valid?.size;
      this.fillSelect(this.cards[index].size, [...new Set(variants.map(variant => variant.size))].map(size => ({value: size, label: `${size} cm`, disabled: !variants.some(variant => variant.size === size && variant.available !== false)})), selection.size);
    }
    selectColor(index) {
      this.selections[index].color = this.cards[index].color.value;
      this.fillSizes(index);
      this.update();
    }
    selectSize(index) {
      const selection = this.selections[index];
      selection.size = this.cards[index].size.value;
      selection.variant = selection.material.variants.find(variant => variant.color === selection.color && variant.size === selection.size);
      this.update();
    }
    update() {
      if (!this.catalog || !this.money) return;
      this.status.textContent = '';
      delete this.status.dataset.error;
      let available = this.dataFresh;
      if (this.mode === 'pillows') {
        const variant = this.selectedPillow;
        const source = this.sourceVariants?.get(String(variant.sourceVariantId));
        this.compare = this.useMarketPrices ? source ? source.price * 2 : null : variant.comparePrice;
        this.selectedBundle = {variantId: variant.id, price: variant.price};
        available = available && (this.useMarketPrices ? source?.available && (source.inventory_quantity === undefined || source.inventory_quantity >= 2) : variant.available !== false);
        this.setImage(this.querySelector('[data-duo-hero]'), variant.image || this.model.image, `${this.model.title} — ${variant.size} cm`);
      } else {
        if (this.selections.some(selection => !selection.variant)) return;
        const sources = this.selections.map(selection => this.useMarketPrices ? this.sourceVariants?.get(String(selection.variant.id)) : selection.variant);
        this.compare = sources.every(source => isPrice(source?.price)) ? sources.reduce((sum, source) => sum + source.price, 0) : null;
        this.selectedBundle = this.bundleMap.get(pairKey(this.selections.map(selection => selection.variant.id)));
        available = available && sources.every(source => source?.available !== false && isPrice(source?.price)) && (this.useMarketPrices || this.selectedBundle?.available !== false);
        this.selections.forEach((selection, index) => {
          this.cards[index].unitPrice.textContent = `${this.moneyText(sources[index]?.price)} ${this.strings.each}`;
          this.setImage(this.cards[index].previewImage, selection.variant.image || selection.material.image, `${selection.material.title} — ${this.colorLabel(selection.color)} — ${selection.size} cm`);
          this.cards[index].previewCaption.textContent = `${selection.material.title} · ${this.colorLabel(selection.color)} · ${selection.size}`;
        });
        const composition = this.querySelector('[data-duo-composition]');
        const list = element('ul');
        this.selections.forEach((selection, index) => list.append(element('li', '', `${this.strings.case_number.replace('__NUMBER__', index + 1)} : ${selection.material.title}, ${this.colorLabel(selection.color)}, ${selection.size} cm`)));
        composition.replaceChildren(element('p', '', this.strings.cases_composition), list);
      }
      this.validId = /^\d+$/.test(String(this.selectedBundle?.variantId || ''));
      if (this.useMarketPrices && this.validId && this.currencySupported) this.loadParentPrice(this.selectedBundle);
      const parent = this.useMarketPrices ? this.parentPrices.get(String(this.selectedBundle?.variantId)) : this.selectedBundle;
      this.total = parent?.price ?? null;
      this.pricePending = this.useMarketPrices && this.validId && this.currencySupported && this.parentRequests.has(String(this.selectedBundle.variantId)) && !this.parentPrices.has(String(this.selectedBundle.variantId));
      available = available && (!this.useMarketPrices || parent?.available === true);
      this.querySelector('[data-duo-price]').textContent = this.moneyText(this.total);
      this.querySelector('[data-duo-compare]').textContent = this.moneyText(this.compare);
      const saving = this.querySelector('[data-duo-saving]');
      saving.hidden = !isPrice(this.compare) || !isPrice(this.total) || this.compare <= this.total;
      saving.textContent = saving.hidden ? '' : this.strings.saving.replace('__AMOUNT__', this.moneyText(this.compare - this.total));
      this.validPrice = isPrice(this.total) && this.total > 0 && isPrice(this.compare) && this.compare >= this.total && (this.currency !== this.catalog.currency || this.total === duoPrice(this.compare) && this.selectedBundle?.price === this.total);
      this.canBuy = !this.preview && available && this.validPrice && this.validId && this.currencySupported;
      this.addButton.disabled = !this.canBuy || this.busy;
      this.addButton.textContent = this.pricePending ? this.strings.loading || this.strings.unavailable : isPrice(this.total) ? this.strings.add.replace('__PRICE__', this.moneyText(this.total)) : this.strings.unavailable;
      this.querySelector('[data-duo-availability]').textContent = this.preview || this.pricePending ? '' : this.canBuy ? this.strings.available : this.strings.unavailable;
      if (!this.currencySupported) { this.status.textContent = this.strings.currency_unavailable; this.status.dataset.error = ''; }
      else if (this.useMarketPrices && !this.pricePending && (!this.dataFresh || !parent)) { this.status.textContent = this.strings.load_error; this.status.dataset.error = ''; }
    }
    async add() {
      if (!this.canBuy || this.busy) return;
      this.busy = true;
      this.addButton.disabled = true;
      this.addButton.textContent = this.strings.adding;
      this.status.textContent = '';
      const properties = {_NoctuaDuo: '1'};
      if (this.mode === 'cases') this.selections.forEach((selection, index) => {
        properties[this.strings.case_number.replace('__NUMBER__', index + 1)] = `${selection.material.title} / ${this.colorLabel(selection.color)} / ${selection.size} cm`;
      });
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({items: [{id: Number(this.selectedBundle.variantId), quantity: 1, properties}]})
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error('Cart rejected');
        this.status.textContent = this.strings.cart_success;
        window.location.assign(this.dataset.cartUrl);
      } catch (_) {
        this.busy = false;
        this.update();
        this.status.textContent = this.strings.add_error;
        this.status.dataset.error = '';
      }
    }
  }
  customElements.define('nx-duo-product', NoctuaDuo);
})();
